<template>
  <div
    class="relative w-full overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-2xl transition-all duration-500 dark:border-white/10 dark:bg-[#09090b] sm:rounded-[32px]">
    <header
      class="relative z-20 flex h-16 items-center justify-between border-b border-zinc-200 bg-white/80 px-4 backdrop-blur-md transition-colors duration-300 dark:border-white/5 dark:bg-[#0c0c0e]/80 sm:px-6">
      <div class="flex items-center gap-3">
        <div
          class="group flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-lg shadow-indigo-500/20 transition-transform duration-500 hover:rotate-12">
          <span class="material-symbols-rounded text-lg">smart_display</span>
        </div>
        <div>
          <h2 class="text-sm font-bold tracking-tight text-zinc-900 dark:text-zinc-100">Video Academy</h2>
          <p class="hidden text-[10px] text-zinc-500 sm:block">NexGridCo Resources</p>
        </div>
      </div>
    </header>

    <main class="relative h-[800px] overflow-hidden bg-zinc-50/50 transition-colors duration-500 dark:bg-[#050505]">
      <div
        class="pointer-events-none absolute left-[-10%] top-[-10%] h-[500px] w-[500px] rounded-full bg-indigo-500/10 blur-[100px] dark:bg-indigo-500/5">
      </div>
      <div
        class="pointer-events-none absolute bottom-[-10%] right-[-10%] h-[500px] w-[500px] rounded-full bg-emerald-500/10 blur-[100px] dark:bg-emerald-500/5">
      </div>

      <div class="custom-scrollbar h-full overflow-y-auto p-4 sm:p-6">
        <div class="space-y-8">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">All Tutorials</h3>
              <p class="text-xs text-zinc-500">Hover to preview (when available), click to watch.</p>
            </div>
            <span class="rounded-lg bg-zinc-100 px-2 py-1 text-[10px] font-bold text-zinc-500 dark:bg-white/5">
              {{ tutorials.length }} Items
            </span>
          </div>

          <div v-if="tutorials.length > 0" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <article v-for="(video, idx) in tutorials" :key="video.id"
              class="group relative flex flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition-all duration-300 dark:border-white/5 dark:bg-[#0c0c0e]"
              :class="video.hasVideo ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl' : 'cursor-default opacity-95'"
              :style="{ animation: `enter-up 0.5s ease-out ${idx * 80}ms backwards` }"
              @mouseenter="video.hasVideo && playPreview(video.id, $event)"
              @mouseleave="video.hasVideo && stopPreview(video.id, $event)"
              @click="video.hasVideo ? openVideo(video) : null">
              <div class="relative aspect-video w-full overflow-hidden bg-zinc-100 dark:bg-zinc-900">
                <video v-if="video.hasVideo" :src="video.src" :poster="video.thumbnail" muted loop playsinline
                  preload="metadata" class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                  :class="isPlaying(video.id) ? 'opacity-100' : 'opacity-0'"></video>

                <div class="absolute inset-0 transition-opacity duration-500"
                  :class="isPlaying(video.id) ? 'opacity-0' : 'opacity-100'">
                  <img :src="video.thumbnail" class="h-full w-full object-cover transition-transform duration-700"
                    :class="video.hasVideo ? 'group-hover:scale-105' : ''" alt="Thumbnail" />

                  <div class="absolute inset-0 flex items-center justify-center transition-colors"
                    :class="video.hasVideo ? 'bg-black/10 group-hover:bg-transparent' : 'bg-black/15'">
                    <div
                      class="flex h-12 w-12 items-center justify-center rounded-full shadow-lg backdrop-blur transition-all duration-300 dark:text-white"
                      :class="video.hasVideo ? 'bg-white/90 text-black group-hover:scale-110 dark:bg-black/80' : 'bg-white/70 text-black dark:bg-black/70'">
                      <span class="material-symbols-rounded text-2xl">
                        {{ video.hasVideo ? "play_arrow" : "schedule" }}
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="video.hasVideo"
                  class="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur-md transition-opacity duration-300"
                  :class="isPlaying(video.id) ? 'opacity-0' : 'opacity-100'">
                  Preview
                </div>

                <div v-else
                  class="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur-md">
                  Soon
                </div>
              </div>

              <div class="flex flex-1 flex-col p-4">
                <div class="mb-2 flex items-center gap-2">
                  <span class="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                    :class="video.hasVideo ? 'bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-400' : 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300'">
                    {{ video.hasVideo ? "Asset" : "Coming soon" }}
                  </span>
                </div>

                <h4 class="mb-1 text-sm font-bold leading-tight text-zinc-900 dark:text-zinc-100 capitalize">
                  {{ video.title }}
                </h4>

                <div class="mt-auto pt-4">
                  <div class="mt-1 flex justify-between text-[9px] font-medium text-zinc-400">
                    <span class="transition-colors" :class="video.hasVideo ? 'group-hover:text-indigo-500' : ''">
                      {{ video.hasVideo ? "Watch Full Video" : "Link will be added tomorrow" }}
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div v-else
            class="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-zinc-200 text-xs text-zinc-400 dark:border-white/10">
            <span class="material-symbols-rounded text-2xl">folder_off</span>
            <span>No items configured</span>
          </div>
        </div>
      </div>
    </main>

    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0"
        enter-to-class="opacity-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <div v-if="selectedVideo"
          class="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4 sm:p-8"
          @click.self="closeVideo">
          <button @click="closeVideo"
            class="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
            <span class="material-symbols-rounded">close</span>
          </button>

          <div class="relative w-full max-w-5xl overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-white/10">
            <video :src="selectedVideo.src" :poster="selectedVideo.thumbnail" controls autoplay playsinline
              preload="metadata" class="w-full max-h-[80vh] object-contain"></video>
            <div class="bg-zinc-900/80 p-4 backdrop-blur-md">
              <h3 class="text-lg font-bold text-white capitalize">{{ selectedVideo.title }}</h3>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"

const playingId = ref(null)
const selectedVideo = ref(null)

const defaultPlaceholder =
  "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1000&auto=format&fit=crop"

const tutorialsData = [

  {
    fileName: "Dashboard.mp4",
    title: "Dashboard",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789599550697-Dashboard.mp4",
    thumbnail: "Dashboard.png",
  },
  {
    fileName: "GenerateAI.mp4",
    title: "Generate AI",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789599586494-GenerateAI.mp4",
    thumbnail: "GenerateAI.png",
  },
  {
    fileName: "ChatAI.mp4",
    title: "Chat AI",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789599537746-ChatAI.mp4",
    thumbnail: "ChatAI.png",
  },
  {
    fileName: "PaletteStudio.mp4",
    title: "Palette Studio",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789958420087-PaletteStudio.mp4",
    thumbnail: "PaletteStudio.png",
  },
  {
    fileName: "SkyStudio.mp4",
    title: "Sky Studio",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789958454076-SkyStudio.mp4",
    thumbnail: "SkyStudio.png",
  },
  {
    fileName: "GradientCraft.mp4",
    title: "Gradient Craft",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789599671987-GradientCraft.mp4",
    thumbnail: "GradientCraft.png",
  },
  {
    fileName: "TextureMaker.mp4",
    title: "Texture Maker",
    src: "https://media.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1790220990887-Studio%20Texture.mp4",
    thumbnail: "TextureMaker.png",
  },
  {
    fileName: "Accessibility.mp4",
    title: "Accessibility",
    src: "https://media.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789599488811-Accessibility.mp4",
    thumbnail: "Accessibility.png",
  },
    {
    fileName: "TokenStudio.mp4",
    title: "Token Studio",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789958562246-TokenStudio.mp4",
    thumbnail: "TokenStudio.png",
  },

  {
    fileName: "UIPlaygroud.mp4",
    title: "UI Playground",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789958573502-UIPlayground.mp4",
    thumbnail: "UIPlaygroud.png",
  },
    {
    fileName: "Library.mp4",
    title: "Library",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789688756891-Library.mp4",
    thumbnail: "Library.png",
  },
  {
    fileName: "Subscription.mp4",
    title: "Subscription",
    src: "https://upload.nexgridco.com/storage/users/f807769c-694a-4ac5-8d1a-a21d131d70d2/gallery/NexgridCo/1789958530282-Subscription.mp4",
    thumbnail: "Subscription.png",
  },
]

const imageContext = require.context("@/assets", false, /\.(png|jpe?g|webp)$/)

const tutorials = computed(() => {
  return tutorialsData.map((v, index) => {
    const thumbKey = imageContext.keys().find((k) => k.replace("./", "") === v.thumbnail) || null

    const hasVideo = Boolean(v.src && String(v.src).trim().length > 0)

    return {
      id: index,
      fileName: v.fileName,
      title: v.title,
      src: v.src,
      hasVideo,
      thumbnail: thumbKey ? imageContext(thumbKey) : defaultPlaceholder,
    }
  })
})

function playPreview(id, event) {
  playingId.value = id
  const videoEl = event.currentTarget.querySelector("video")
  if (videoEl) videoEl.play().catch(() => { })
}

function stopPreview(id, event) {
  playingId.value = null
  const videoEl = event.currentTarget.querySelector("video")
  if (videoEl) {
    videoEl.pause()
    videoEl.currentTime = 0
  }
}

function isPlaying(id) {
  return playingId.value === id
}

function openVideo(video) {
  selectedVideo.value = video
}

function closeVideo() {
  selectedVideo.value = null
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(161, 161, 170, 0.2);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(161, 161, 170, 0.4);
}

@keyframes enter-up {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
